import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({
  theme: 'dark',
  toggleTheme: () => {},
  isTransitioning: false,
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('portfolio-theme');
    if (savedTheme) return savedTheme;
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';

    return 'light';
  });
  
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('transition-colors', 'duration-300');
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    localStorage.setItem('portfolio-theme', theme);
    setTimeout(() => {
      root.classList.add('transition-colors', 'duration-300');
    }, 0);
  }, [theme]);

  const toggleTheme = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    const overlay = document.createElement('div');

    overlay.className = 'theme-transition-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      pointer-events: none;
      clip-path: circle(0% at 100% 0%);
      background: ${nextTheme === 'dark' ? '#0a0e1a' : '#ffffff'};
      opacity: 0.95;
      transition: clip-path 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    `;
    
    document.body.appendChild(overlay);
    void overlay.offsetWidth;
    overlay.style.clipPath = 'circle(150% at 50% 0%)';

    setTimeout(() => {
      setTheme(nextTheme);
    }, 300);
    
    setTimeout(() => {
      document.body.removeChild(overlay);
      setIsTransitioning(false);
    }, 600);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isTransitioning }}>
      {children}
    </ThemeContext.Provider>
  );
};